import { Children, ReactNode, cloneElement, isValidElement } from "react";
import { useInView } from "@/hooks/useInView";
import { cn } from "@/lib/utils";

interface RevealProps {
  children: ReactNode;
  /** Stagger children by N ms; 0 = no stagger (default). */
  stagger?: number;
  /** Additional delay applied after stagger. */
  delay?: number;
  className?: string;
  as?: keyof JSX.IntrinsicElements;
}

/**
 * Fades + rises children when they enter the viewport.
 * Drop-in: <Reveal>...</Reveal>. With stagger, direct children animate in sequence.
 */
export function Reveal({
  children,
  stagger = 0,
  delay = 0,
  className,
  as: Tag = "div",
}: RevealProps) {
  const [ref, inView] = useInView<HTMLDivElement>();

  const Comp: any = Tag;

  if (stagger > 0) {
    const arr = Children.toArray(children);
    return (
      <Comp ref={ref as any} className={cn(className)}>
        {arr.map((child, i) => {
          const style = {
            transitionDelay: inView ? `${delay + i * stagger}ms` : "0ms",
          } as React.CSSProperties;
          const cls = cn(
            "transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none",
            inView
              ? "opacity-100 translate-y-0 scale-100"
              : "opacity-0 translate-y-3 scale-[0.985]"
          );
          if (isValidElement(child)) {
            const existing = (child.props as any).className ?? "";
            return cloneElement(child as any, {
              key: (child as any).key ?? i,
              className: cn(existing, cls),
              style: { ...((child.props as any).style ?? {}), ...style },
            });
          }
          return (
            <span key={i} className={cls} style={style}>
              {child}
            </span>
          );
        })}
      </Comp>
    );
  }

  return (
    <Comp
      ref={ref as any}
      style={{ transitionDelay: inView ? `${delay}ms` : "0ms" }}
      className={cn(
        "transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none",
        inView ? "opacity-100 translate-y-0 scale-100" : "opacity-0 translate-y-3 scale-[0.985]",
        className
      )}
    >
      {children}
    </Comp>
  );
}
